import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';

import { skipToken } from '@reduxjs/toolkit/query';
import { isMasterProjectId, setSelectedProject, useGetProjectsQuery } from 'features/projects';
import { DEFAULT_COUNTRY_CODE, getCommuneLabel, isCountryCode } from 'shared/config/countries';
import { useAppDispatch } from 'shared/hooks/redux';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

import { useCreateSubProjectMutation, useGetMasterWithSubsQuery } from '../api/master.api';
import { CountryCode, CreateSubProjectPayload, FEATURE_FLAG_KEYS, ProjectFeatureFlags } from '../api/types';
import { getPromptTemplateById, PROMPT_TEMPLATES } from '../model/promptTemplates';
import FeatureToggleGroup, { DEFAULT_FEATURE_FLAGS } from '../ui/FeatureToggleGroup';
import PromptTypeSelect from '../ui/PromptTypeSelect';

const extractErrorMessage = (e: unknown): string => {
  if (e instanceof Error) return e.message;
  if (typeof e === 'object' && e !== null && 'data' in e) {
    const { data } = e as { data: unknown };
    if (typeof data === 'string') return data;
  }
  return 'No se pudo crear el subproyecto. Intenta nuevamente.';
};

const flagsFromMaster = (master: Partial<ProjectFeatureFlags> | undefined): ProjectFeatureFlags =>
  FEATURE_FLAG_KEYS.reduce((acc, key) => {
    acc[key] = master?.[key] ?? DEFAULT_FEATURE_FLAGS[key];
    return acc;
  }, {} as ProjectFeatureFlags);

const CreateSubProjectPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { masterProjectId } = useParams();
  const numericMasterProjectId = Number(masterProjectId);
  const { data: projects = [], isLoading: isProjectsLoading } = useGetProjectsQuery();
  const masterId =
    Number.isFinite(numericMasterProjectId) && isMasterProjectId(projects, numericMasterProjectId)
      ? numericMasterProjectId
      : null;
  const { data: master, isLoading: loadingMaster } = useGetMasterWithSubsQuery(masterId ?? skipToken);
  const [createSubProject, { isLoading }] = useCreateSubProjectMutation();

  const initialPromptTemplate = getPromptTemplateById('default') ?? PROMPT_TEMPLATES[0];

  const [name, setName] = useState('');
  const [commune, setCommune] = useState('');
  const [promptTypeId, setPromptTypeId] = useState<string>(initialPromptTemplate?.id ?? '');
  const [prompt, setPrompt] = useState<string>(initialPromptTemplate?.prompt ?? '');
  const [features, setFeatures] = useState<ProjectFeatureFlags>(DEFAULT_FEATURE_FLAGS);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [hasInheritedFlags, setHasInheritedFlags] = useState(false);

  useEffect(() => {
    if (!master || hasInheritedFlags) return;
    setFeatures(flagsFromMaster(master));
    setHasInheritedFlags(true);
  }, [master, hasInheritedFlags]);

  const masterCountry: CountryCode = isCountryCode(master?.countryCode) ? master.countryCode : DEFAULT_COUNTRY_CODE;
  const communeLabel = getCommuneLabel(masterCountry);

  const isMasterValid = master?.masterProjectId === null;
  const nameValid = name.trim().length > 0;
  const promptValid = prompt.trim().length > 0;
  const canSubmit = nameValid && promptValid && isMasterValid && !isLoading;

  const handlePromptType = (id: string) => {
    setPromptTypeId(id);
    const template = getPromptTemplateById(id);
    if (template) setPrompt(template.prompt);
  };

  if (isProjectsLoading) {
    return (
      <div className="mx-auto max-w-4xl">
        <p className="text-sm text-text-muted">Cargando inmobiliaria...</p>
      </div>
    );
  }

  if (masterId === null) {
    return <Navigate to="/project" replace />;
  }

  const handleSubmit = async () => {
    if (!master || !isMasterValid) return;
    setErrorMessage(null);
    const trimmedCommune = commune.trim();
    const payload: CreateSubProjectPayload = {
      ...features,
      name: name.trim(),
      prompt,
      ...(trimmedCommune ? { commune: trimmedCommune } : {}),
      phoneNumber: '',
      countryCode: masterCountry,
      masterProjectId: master.id,
      pusherIdentification: '',
      devUrl: '',
      scheduleVisitInstructions: '',
      availableDates: [],
      askClaveUnica: false,
      disableResponseDelay: false,
      guardrailMode: 'disabled',
    };
    try {
      await createSubProject(payload).unwrap();
      dispatch(setSelectedProject(master.id));
      void navigate(`/project/${master.id}/config`);
    } catch (e) {
      setErrorMessage(extractErrorMessage(e));
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <header>
        <h1 className="font-display text-2xl font-bold">Crear Subproyecto</h1>
        <p className="text-sm text-text-muted">
          Inmobiliaria:{' '}
          <span className="font-medium text-text-default">{master?.name ?? (loadingMaster ? 'Cargando…' : '—')}</span>
        </p>
      </header>

      {!loadingMaster && !isMasterValid && (
        <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
          El ID de la URL no corresponde a una inmobiliaria válida.
        </p>
      )}

      <section className="space-y-6 rounded-md border border-border p-6">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="sub-name">Nombre del proyecto</Label>
            <Input
              id="sub-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej. Edificio Aurora"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="sub-commune">{communeLabel}</Label>
            <Input
              id="sub-commune"
              value={commune}
              onChange={(e) => setCommune(e.target.value)}
              placeholder="Ej: Las Condes"
            />
          </div>
          <PromptTypeSelect id="sub-prompt-type" value={promptTypeId} onChange={handlePromptType} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="sub-prompt">Prompt</Label>
          <Textarea id="sub-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={5} />
          <p className="text-xs text-text-muted">Se completa al elegir un tipo; puedes editarlo manualmente.</p>
        </div>
        <FeatureToggleGroup value={features} onChange={setFeatures} />
      </section>

      {errorMessage && (
        <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
          {errorMessage}
        </p>
      )}

      <div className="flex items-center justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            void navigate(-1);
          }}
          disabled={isLoading}
        >
          Cancelar
        </Button>
        <Button
          type="button"
          onClick={() => {
            void handleSubmit();
          }}
          disabled={!canSubmit}
        >
          {isLoading ? 'Creando…' : 'Crear subproyecto'}
        </Button>
      </div>
    </div>
  );
};

export default CreateSubProjectPage;
